/*
 * File: FormFieldControl.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Iterates over the form fields and returns the input type, label and error message
 */
import React from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControl from "@material-ui/core/FormControl";
import InputSwitchStatement from "./InputSwitchStatement";
import { Grid } from "@material-ui/core";

const FormFieldControl = ({ field, data, parentOnChange }) => {
  /**
   * Called when a child input component gets changed.
   *
   * Sets the key, value from the input target and calls the parent change
   *
   * @param {Object} event
   */
  const onChange = event => {
    const key = event.target.name;
    let val = event.target.value;

    // set val to boolean of checked
    if (event.target.type === "checkbox") {
      val = event.target.checked;
    }

    if (field.type === "integer" || field.type === "decimal") {
      val = Number(val);
    }

    // Gets the selected option value and updates it in a model through option key
    if (field.type === "select" && field.optionKey) {
      if (val === "0" || !val) {
        parentOnChange(field.optionKey, "");
      } else {
        const option = field.options.find(o => o.id === val);
        if (option) parentOnChange(field.optionKey, option.name);
      }
    }

    parentOnChange(key, val);
  };

  const switchOnChange = key => {
    data.handleSwitchToggle(Number(key.target.id), key.target.checked);
  };

  /* The downshift library doesn't create an event when selecting an item.  Instead it returns the selected item */
  const autoCompleteOnChange = (name, selectedResult) => {
    parentOnChange(name, selectedResult);
  };

  const dateOnChange = date => {
    parentOnChange(field.name, date);
  };

  const timeOnChange = time => {
    parentOnChange(field.name, time);
  };

  // Material UI error prop expects a bool
  const error = Boolean(field.errorMessage);
  let value = "";
  if (field.name === "phone1Type" && data.phone1) {
    value = data.phone1.type;
  } else if (field.name === "phone2Type" && data.phone2) {
    value = data.phone2.type;
  } else if (field.name === "phone3Type" && data.phone3) {
    value = data.phone3.type;
  } else if (field.type === "switch") {
    value = field.checked;
  } else if (
    field.name === "accountProvider" ||
    field.name === "patientProvider"
  ) {
    const subModel = data[field.name];
    if (subModel) value = subModel["userId"];
  } else if (field.name === "patientOffice" || field.name === "feeSchedule") {
    const subModel = data[field.name];
    if (subModel) value = subModel["id"];
  } else {
    value = data[field.name];
  }
  if (value === null || value === undefined || Number.isNaN(value)) {
    value = "";
  }

  return (
    <Grid item {...field.gridItemProps}>
      <FormControl
        required={field.required}
        error={error}
        margin="normal"
        fullWidth
        key={field.name}
      >
        <InputSwitchStatement
          field={field}
          error={error}
          value={value}
          parentOnBlur={() => field.validateFieldForBlur(data)}
          parentOnChange={onChange}
          autoCompleteOnChange={autoCompleteOnChange}
          switchOnChange={switchOnChange}
          dateOnChange={dateOnChange}
          timeOnChange={timeOnChange}
        />

        {field.errorMessage && (
          <FormHelperText>{field.errorMessage}</FormHelperText>
        )}
      </FormControl>
    </Grid>
  );
};

FormFieldControl.propTypes = {
  field: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  parentOnChange: PropTypes.func.isRequired
};

export default observer(FormFieldControl);
